import { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';

import { Button } from '@shared';
import { t } from '@/i18n';

/**
 * 루트 라우트의 errorElement — 앱 전역의 라우트 에러를 잡는다.
 *
 * 1) 배포 후 이전 빌드의 JS 청크가 사라져 lazy import가 실패한 경우(ChunkLoadError)는
 *    자동으로 1회 새로고침해 최신 빌드를 로드한다(무한 새로고침 방지: sessionStorage 1회 가드).
 * 2) 그 외의 렌더/데이터 에러(TRAD-2045: notificationType 계약 불일치로 인한 throw 등)는
 *    "새 버전 배포"로 오표시하지 않고 일반 에러 UI를 보여주며, raw error를 콘솔에 로깅한다
 *    (전역 에러 처리 규칙 — 에러를 삼키지 않는다). 오표시가 실제 원인 진단을 가로막던 문제 수정(TRAD-2046).
 */
const RELOAD_KEY = 'lazy-load-reload';

const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error &&
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(
    error.message,
  );

export const LazyLoadError = () => {
  const error = useRouteError();
  const isChunkError = isChunkLoadError(error);

  useEffect(() => {
    if (isChunkError) {
      const hasReloaded = sessionStorage.getItem(RELOAD_KEY);
      if (!hasReloaded) {
        sessionStorage.setItem(RELOAD_KEY, 'true');
        window.location.reload();
        return;
      }
      sessionStorage.removeItem(RELOAD_KEY);
      return;
    }
    // 청크 에러가 아닌 렌더/데이터 에러 — raw error를 반드시 로깅(원인 진단 가능하게)
    console.error('[route-error] non-chunk error caught by root errorElement', error);
  }, [isChunkError, error]);

  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4 text-center px-4">
      <p className="text-lg font-semibold text-foreground">
        {isChunkError ? t('error.deploy.title') : t('error.generic.title')}
      </p>
      <p className="text-sm text-muted-foreground">{isChunkError ? t('error.deploy.desc') : t('error.generic.desc')}</p>
      <Button
        type="button"
        onClick={() => {
          sessionStorage.removeItem(RELOAD_KEY);
          window.location.reload();
        }}
        className="rounded-lg"
      >
        {t('error.retry')}
      </Button>
    </div>
  );
};
